import axios from 'axios';
import { Project, ProjectOtherCost, ProjectType, ProjectStatus, ProjectHistoryEntry } from '../types/Project';
import { Task } from '../types/Task';

const BASE_URL = process.env.REACT_APP_API_URL;

export interface ProjectListResponse {
  data: Project[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface ProjectFilters {
  /**
   * Multiselect filters. Sent as a comma-separated string ("5,7"); a single bare value ("5") is
   * still valid and the server treats it as a one-element list. Empty string means "no filter".
   * Kept as `string` (not `string[]`) so the value round-trips through the URL query params
   * unchanged — the projects list URL is the source of truth for this page.
   */
  serviceArea?: string;
  /** Comma-separated project statuses, e.g. "active,planned". See serviceArea. */
  status?: string;
  /** Comma-separated project types, e.g. "Quantified,Routine". See serviceArea. */
  type?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  taskNumber?: string;
  /** Server-side sort (allowlisted columns): projectNumber, jobNumber, name, type, status, serviceArea, startDate. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface TaskPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface ProjectWithTaskPagination extends Project {
  taskPagination?: TaskPagination;
}

export interface ProjectWorksTaskListResponse {
  data: Task[];
  pagination: TaskPagination;
}

export interface ProjectWorksTaskFilters {
  serviceArea?: string;
  projectId?: string;
  taskType?: string;
  status?: string | string[];
  foremanArea?: string | string[];
  search?: string;
  contractYear?: string;
  activityId?: string;
  taskId?: string;
}

export interface ProjectWorksTaskFilterOptions {
  statuses: string[];
  taskTypes: string[];
  serviceAreas: Array<{ id: number; name: string }>;
  contractYears: string[];
  projects: Array<{ id: number; name: string; projectNumber: string }>;
  foremanAreas: Array<{ id: number; name: string; serviceAreaId?: number; serviceAreaName?: string }>;
  activities: Array<{ id: number; activityName: string; activityNumber: string }>;
}

class ProjectService {
  async getAllProjects(): Promise<Project[]> {
    try {
      const response = await axios.get(`${BASE_URL}/projects`);
      // Handle both old and new API response formats
      return Array.isArray(response.data) ? response.data : response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProjectsForTaskModal(serviceArea?: string, status?: string): Promise<Project[]> {
    try {
      const params = new URLSearchParams();
      if (serviceArea) {
        params.append('serviceArea', serviceArea);
      }
      if (status) {
        params.append('status', status);
      }

      const url = `${BASE_URL}/projects/task-modal-projects${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProjectsPaginated(
    page: number = 1, 
    pageSize: number = 10, 
    filters: ProjectFilters = {}
  ): Promise<ProjectListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== undefined && value !== '')
        )
      });

      const response = await axios.get(`${BASE_URL}/projects?${params}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProjectFilterOptions(): Promise<{
    types: string[];
    statuses: string[];
    serviceAreas: { id: number; name: string }[];
  }> {
    try {
      const response = await axios.get(`${BASE_URL}/projects/filter-options`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProjectWorksTasksPaginated(
    page: number = 1,
    pageSize: number = 20,
    filters: ProjectWorksTaskFilters = {}
  ): Promise<ProjectWorksTaskListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });
      Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
          return;
        }
        if (Array.isArray(value)) {
          params.set(key, value.join(','));
        } else {
          params.set(key, value.toString());
        }
      });
      const response = await axios.get(`${BASE_URL}/projects/tasks?${params}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProjectWorksTaskFilterOptions(): Promise<ProjectWorksTaskFilterOptions> {
    try {
      const response = await axios.get(`${BASE_URL}/projects/tasks/filter-options`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProjectById(
    id: number, 
    taskPage: number = 1, 
    taskPageSize: number = 20,
    taskFilters: { taskType?: string; taskStatus?: string; taskSearch?: string } = {}
  ): Promise<ProjectWithTaskPagination> {
    try {
      const params = new URLSearchParams({
        taskPage: taskPage.toString(),
        taskPageSize: taskPageSize.toString(),
        ...Object.fromEntries(
          Object.entries(taskFilters).filter(([_, value]) => value !== undefined && value !== '')
        )
      });

      const response = await axios.get(`${BASE_URL}/projects/${id}?${params}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProjectHistory(projectId: number): Promise<ProjectHistoryEntry[]> {
    try {
      const response = await axios.get(`${BASE_URL}/projects/${projectId}/history`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async addProjectHistoryComment(projectId: number, payload: { comments: string; userId: number }): Promise<ProjectHistoryEntry[]> {
    try {
      const response = await axios.post(`${BASE_URL}/projects/${projectId}/history-comment`, payload);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createProject(project: Partial<Project>): Promise<Project> {
    try {
      const formattedProject = {
        ...project,
        startDate: project.startDate ? new Date(project.startDate).toISOString() : new Date().toISOString(),
        endDate: project.endDate ? new Date(project.endDate).toISOString() : new Date().toISOString(),
        ownerId: project.ownerId ? parseInt(project.ownerId.toString()) : undefined,
        serviceAreaId: project.serviceAreaId ? parseInt(project.serviceAreaId.toString()) : undefined,
        type: project.type || 'routine',
        projectNumber: undefined, // Let the server generate this
        budget: project.budget ? parseFloat(project.budget.toString()) : 0,
        labourMarkup: project.labourMarkup ? parseFloat(project.labourMarkup.toString()) : 0,
        materialMarkup: project.materialMarkup ? parseFloat(project.materialMarkup.toString()) : 0,
        vehicleMarkup: project.vehicleMarkup ? parseFloat(project.vehicleMarkup.toString()) : 0,
        subcontractorMarkup: project.subcontractorMarkup ? parseFloat(project.subcontractorMarkup.toString()) : 0,
        hiredEquipmentMarkup: project.hiredEquipmentMarkup ? parseFloat(project.hiredEquipmentMarkup.toString()) : 0
      };

      console.log('Sending project data:', formattedProject);
      const response = await axios.post(`${BASE_URL}/projects`, formattedProject);
      return response.data;
    } catch (error) {
      console.error('Project creation error:', error);
      throw this.handleError(error);
    }
  }

  async updateProject(id: number, project: Partial<Project>): Promise<Project> {
    try {
      const formattedProject = {
        ...project,
        startDate: project.startDate ? new Date(project.startDate).toISOString() : undefined,
        endDate: project.endDate ? new Date(project.endDate).toISOString() : undefined,
        ownerId: project.ownerId ? parseInt(project.ownerId.toString()) : undefined,
        serviceAreaId: project.serviceAreaId ? parseInt(project.serviceAreaId.toString()) : undefined,
        type: project.type || 'routine',
        projectNumber: project.projectNumber ? project.projectNumber : undefined,
        budget: project.budget !== undefined && project.budget !== null ? parseFloat(project.budget.toString()) : undefined,
        labourMarkup: project.labourMarkup !== undefined && project.labourMarkup !== null ? parseFloat(project.labourMarkup.toString()) : undefined,
        materialMarkup: project.materialMarkup !== undefined && project.materialMarkup !== null ? parseFloat(project.materialMarkup.toString()) : undefined,
        vehicleMarkup: project.vehicleMarkup !== undefined && project.vehicleMarkup !== null ? parseFloat(project.vehicleMarkup.toString()) : undefined,
        subcontractorMarkup: project.subcontractorMarkup !== undefined && project.subcontractorMarkup !== null ? parseFloat(project.subcontractorMarkup.toString()) : undefined,
        hiredEquipmentMarkup: project.hiredEquipmentMarkup !== undefined && project.hiredEquipmentMarkup !== null ? parseFloat(project.hiredEquipmentMarkup.toString()) : undefined
      };

      console.log('Updating project with data:', formattedProject);
      const response = await axios.put(`${BASE_URL}/projects/${id}`, formattedProject);
      return response.data;
    } catch (error) {
      console.error('Project update error:', error);
      throw this.handleError(error);
    }
  }

  async closeProject(id: number): Promise<Project> {
    try {
      const response = await axios.put(`${BASE_URL}/projects/${id}`, {
        status: 'completed'
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async closeAllTasks(
    projectId: number, 
    comments: string, 
    defaultCompletionDate: string, 
    quantifiedTaskAction: 'INTERNAL_REVIEW' | 'CLOSED'
  ): Promise<Project> {
    try {
      const response = await axios.post(`${BASE_URL}/projects/${projectId}/close-all-tasks`, {
        comments,
        defaultCompletionDate,
        quantifiedTaskAction
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async cancelProject(projectId: number, reason?: string): Promise<Project> {
    try {
      const response = await axios.post(`${BASE_URL}/projects/${projectId}/cancel`, {
        reason: reason || ''
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Project Other Costs methods
  async getProjectOtherCosts(projectId: number): Promise<ProjectOtherCost[]> {
    try {
      const response = await axios.get(`${BASE_URL}/projects/${projectId}/other-costs`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createProjectOtherCost(projectId: number, cost: Partial<ProjectOtherCost>): Promise<Project> {
    try {
      const requestData: any = {
        date: cost.date ? new Date(cost.date).toISOString() : new Date().toISOString(),
        description: cost.description || ''
      };

      // Include new fields if provided
      if (cost.dollarAmount !== undefined) {
        requestData.dollarAmount = parseFloat(cost.dollarAmount.toString());
      }
      if (cost.markup !== undefined) {
        requestData.markup = parseFloat(cost.markup.toString());
      }
      // Fallback to old amount field for backward compatibility
      if (cost.amount !== undefined) {
        requestData.amount = parseFloat(cost.amount.toString());
      }

      const response = await axios.post(`${BASE_URL}/projects/${projectId}/other-costs`, requestData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateProjectOtherCost(projectId: number, costId: number, cost: Partial<ProjectOtherCost>): Promise<Project> {
    try {
      const requestData: any = {};

      if (cost.date !== undefined && cost.date !== null) {
        requestData.date = new Date(cost.date).toISOString();
      }
      if (cost.description !== undefined) {
        requestData.description = cost.description;
      }
      
      // Include new fields if provided
      if (cost.dollarAmount !== undefined) {
        requestData.dollarAmount = parseFloat(cost.dollarAmount.toString());
      }
      if (cost.markup !== undefined) {
        requestData.markup = parseFloat(cost.markup.toString());
      }
      // Fallback to old amount field for backward compatibility
      if (cost.amount !== undefined) {
        requestData.amount = parseFloat(cost.amount.toString());
      }

      const response = await axios.put(`${BASE_URL}/projects/${projectId}/other-costs/${costId}`, requestData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteProjectOtherCost(projectId: number, costId: number): Promise<Project> {
    try {
      const response = await axios.delete(`${BASE_URL}/projects/${projectId}/other-costs/${costId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Invoice methods
  async getProjectInvoices(projectId: number): Promise<any[]> {
    try {
      const response = await axios.get(`${BASE_URL}/projects/${projectId}/invoices`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createInvoice(projectId: number, invoiceData: any): Promise<any> {
    try {
      const response = await axios.post(`${BASE_URL}/projects/${projectId}/invoices`, invoiceData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateInvoice(projectId: number, invoiceId: number, invoiceData: any): Promise<any> {
    try {
      const response = await axios.put(`${BASE_URL}/projects/${projectId}/invoices/${invoiceId}`, invoiceData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteInvoice(projectId: number, invoiceId: number): Promise<any> {
    try {
      const response = await axios.delete(`${BASE_URL}/projects/${projectId}/invoices/${invoiceId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async uploadInvoiceAttachment(projectId: number, invoiceId: number, file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${BASE_URL}/projects/${projectId}/invoices/${invoiceId}/attachments`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Vendor invoice methods (invoice-like metadata + PDF attachments)
  async getProjectVendorInvoices(projectId: number): Promise<any[]> {
    try {
      const response = await axios.get(`${BASE_URL}/projects/${projectId}/vendor-invoices`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createVendorInvoice(projectId: number, invoiceData: any): Promise<any> {
    try {
      const response = await axios.post(`${BASE_URL}/projects/${projectId}/vendor-invoices`, invoiceData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateVendorInvoice(projectId: number, vendorInvoiceId: number, invoiceData: any): Promise<any> {
    try {
      const response = await axios.put(
        `${BASE_URL}/projects/${projectId}/vendor-invoices/${vendorInvoiceId}`,
        invoiceData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteVendorInvoice(projectId: number, vendorInvoiceId: number): Promise<any> {
    try {
      const response = await axios.delete(`${BASE_URL}/projects/${projectId}/vendor-invoices/${vendorInvoiceId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async uploadVendorInvoiceAttachment(projectId: number, vendorInvoiceId: number, file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BASE_URL}/projects/${projectId}/vendor-invoices/${vendorInvoiceId}/attachments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Other project documents (PDF + optional notes)
  async getProjectOtherDocuments(projectId: number): Promise<any[]> {
    try {
      const response = await axios.get(`${BASE_URL}/projects/${projectId}/other-documents`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createProjectOtherDocument(
    projectId: number,
    docData: any,
    file: File
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Server expects optional `notes` field (mapped to attachmentNotes)
      if (docData?.attachmentNotes) {
        formData.append('notes', docData.attachmentNotes);
      }

      const response = await axios.post(`${BASE_URL}/projects/${projectId}/other-documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteProjectOtherDocument(projectId: number, docId: number): Promise<any> {
    try {
      const response = await axios.delete(`${BASE_URL}/projects/${projectId}/other-documents/${docId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // H200 Form Upload method
  async uploadH200Form(projectId: number, file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${BASE_URL}/projects/${projectId}/h200-form-upload`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProjectSummary(
    filters: {
      fiscalYears: string[];
      projectTypes: ProjectType[];
      projectStatuses: ProjectStatus[];
      serviceAreaId?: number;
    },
    pagination?: {
      page: number;
      limit: number;
    }
  ): Promise<{ data: any[]; pagination: { page: number; limit: number; total: number; totalPages: number }; totals?: { totalBudget: number; totalCostsUsed: number; totalCostsInAction: number; totalCosts: number; totalValue: number; totalGrossMargin: number; totalGrossMarginPercent: number } }> {
    try {
      const params = new URLSearchParams();
      if (filters.fiscalYears && filters.fiscalYears.length > 0) {
        filters.fiscalYears.forEach(year => params.append('fiscalYear', year));
      }
      if (filters.projectTypes && filters.projectTypes.length > 0) {
        filters.projectTypes.forEach(type => params.append('projectType', type));
      }
      if (filters.projectStatuses && filters.projectStatuses.length > 0) {
        filters.projectStatuses.forEach(status => params.append('projectStatus', status));
      }
      if (filters.serviceAreaId) {
        params.append('serviceAreaId', filters.serviceAreaId.toString());
      }
      if (pagination) {
        params.append('page', pagination.page.toString());
        params.append('limit', pagination.limit.toString());
      }

      const response = await axios.get(`${BASE_URL}/projects/summary?${params}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Error {
    console.error('API Error:', error);
    if (error.response) {
      const errorMessage = error.response.data.details || error.response.data.message || 'An error occurred';
      console.error('Error details:', errorMessage);
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('No response received from server');
    } else {
      throw new Error('Error setting up request');
    }
  }
}

export const projectService = new ProjectService();
export default projectService; 